/**
 * Custom semi-truck icon drawn in lucide's visual style.
 *
 * Built via lucide-react's `createLucideIcon` helper so it inherits
 * every lucide prop (size, color, strokeWidth, absoluteStrokeWidth)
 * and renders interchangeably alongside the stock lucide icons we use
 * elsewhere. Drop-in replacement anywhere a lucide icon goes.
 *
 * ## Why custom
 *
 * Lucide (as of 0.577) doesn't ship a proper tractor-trailer silhouette.
 * Alternatives we considered:
 *   - `Container` — a 3D isometric freight cube (no wheels, no cab)
 *   - `Truck`     — single-unit box truck, already used for our 26'
 *                   Box Truck row, would create a duplicate
 *   - `TruckElectric` — `Truck` plus a lightning bolt
 *
 * The Semi / Gear Truck is the apex of our vehicle fleet and the row
 * most tour managers will click on. It deserves its own silhouette.
 *
 * ## Shape (rev. 4 — 2026-04-19 zoom/scale-up after Chris's "feels small" note)
 *
 * 24×24 viewBox, stroke-based, 2 px stroke, round caps + joins —
 * matching every other lucide vehicle icon in the set:
 *
 *   Trailer:  flat-and-long box on the LEFT (x=2–12, y=4–18, so 10
 *             wide × 14 tall), rounded top corners. Top at y=4 lines
 *             up with lucide's stock `Truck` icon which starts its
 *             body at y=4 — gives the vehicle the same visual
 *             weight as the rest of the icon set.
 *   Gap:      2-unit empty space at x=12–14 (fifth-wheel coupling
 *             zone). Ground visible between the two units, making
 *             this read as a tractor-trailer rather than one truck.
 *   Tractor:  chunky cab on the RIGHT (x=14–22, y=4–18, so 8 wide ×
 *             14 tall), rounded top-left corner, flat sleeper roof
 *             from x=15 to x=19, sloped hood `l3 3` from (19, 4)
 *             down to (22, 7), straight grille face to the ground.
 *             Cab TOP is PARALLEL with the trailer top at y=4 —
 *             standard US road-truck proportions (similar heights).
 *   Wheels:   three circles along y=18 — trailer rear (x=5, r=2,
 *             biggest, anchors the trailer), drive axle (x=16, r=1.5,
 *             under the back of the cab), steer axle (x=20, r=1.5,
 *             under the hood). Intentional size step-down reads as
 *             "bigger trailer tire, smaller tractor wheels" which is
 *             close to real-world.
 *
 * Proportions across revs:
 *   Rev 1: trailer 14w×13h, tractor 6w×5h    ← too boxy trailer
 *   Rev 2: trailer 11w×9h,  tractor 9w×12h   ← tractor towered
 *   Rev 3: trailer 10w×9h,  tractor 8w×9h    ← parallel tops, gap,
 *                                              but only 9 tall so
 *                                              icon read as smaller
 *                                              than Truck/Bus at 16px
 *   Rev 4: trailer 10w×14h, tractor 8w×14h   ← zoomed vertically to
 *          (current)                           match lucide's stock
 *                                              vehicle icons' weight
 *
 * Created 2026-04-19 when iterating on the VehiclePicker icon set.
 */

import { createLucideIcon } from 'lucide-react'

export const SemiTruck = createLucideIcon('semi-truck', [
	// Trailer — flat long box with rounded top corners (x=2–12, y=4–18)
	['path', { d: 'M2 5a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v13H2Z', key: 'trailer' }],
	// Tractor — cab with sleeper roof + sloped hood (x=14–22, y=4–18).
	// Top is parallel with the trailer's top at y=4 so the two units
	// read as a matched pair separated by the coupling gap at x=12–14.
	['path', { d: 'M15 4h4l3 3v11H14V5a1 1 0 0 1 1-1Z', key: 'tractor' }],
	// Wheels along the ground line at y=18
	['circle', { cx: '5', cy: '18', r: '2', key: 'wheel-trailer' }],
	['circle', { cx: '16', cy: '18', r: '1.5', key: 'wheel-drive' }],
	['circle', { cx: '20', cy: '18', r: '1.5', key: 'wheel-steer' }],
])
